import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users as UsersIcon, CreditCard, Dumbbell, ScanFace, Activity, DollarSign, UserCog, LogOut, Menu, MessageCircle, Building2, TrendingUp, ShoppingBag, Cpu, MessageSquareText, Zap, Wallet, Sun, Moon, Settings as SettingsIcon, Map, Video, Trophy, Megaphone, MessageSquare, Globe, BarChart3, ShieldAlert, Shield, Database } from "lucide-react";
import { useState } from "react";
import { useTheme } from "@/lib/theme";
import { MobilePanel } from "@/components/MobilePanel";

const nav = [
  { to: "/mural", icon: Megaphone, label: "Avisos", testid: "nav-avisos", hideForAdmin: true },
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/consolidado", icon: TrendingUp, label: "Consolidado", testid: "nav-consolidated", adminOnly: true },
  { to: "/venda-rapida", icon: Zap, label: "Venda Rápida", testid: "nav-quicksale" },
  { to: "/alunos", icon: UsersIcon, label: "Alunos", testid: "nav-students" },
  { to: "/mapa-alunos", icon: Map, label: "Mapa de Alunos", testid: "nav-students-map" },
  { to: "/planos", icon: CreditCard, label: "Planos", testid: "nav-plans" },
  { to: "/treinos", icon: Dumbbell, label: "Treinos", testid: "nav-workouts" },
  { to: "/biblioteca-exercicios", icon: Video, label: "Biblioteca", testid: "nav-library" },
  { to: "/videos-american-fit", icon: Video, label: "Vídeos American Fit", testid: "nav-af-videos", adminOnly: true },
  { to: "/catraca", icon: ScanFace, label: "Catraca", testid: "nav-turnstile" },
  { to: "/caixa-dia", icon: Wallet, label: "Caixa do Dia", testid: "nav-daily-cash" },
  { to: "/balanco-mensal", icon: TrendingUp, label: "Balanço Mensal", testid: "nav-monthly" },
  { to: "/pdv", icon: ShoppingBag, label: "Loja / PDV", testid: "nav-shop" },
  { to: "/avisos", icon: Megaphone, label: "Avisos (Portal)", testid: "nav-notices" },
  { to: "/feedback-alunos", icon: MessageSquare, label: "Feedback Alunos", testid: "nav-feedback" },
  { to: "/site-vendas", icon: Globe, label: "Site de Vendas", testid: "nav-landing" },
  { to: "/relatorios", icon: BarChart3, label: "Relatórios", testid: "nav-reports" },
  { to: "/auditoria", icon: ShieldAlert, label: "Auditoria", testid: "nav-audit", adminOnly: true },
  { to: "/seguranca", icon: Shield, label: "Segurança", testid: "nav-security", adminOnly: true },
  { to: "/avisos-usuarios", icon: Megaphone, label: "Avisos (Usuários)", testid: "nav-staff-notices", adminOnly: true },
  { to: "/dispositivos", icon: Cpu, label: "Dispositivos", testid: "nav-devices", adminOnly: true },
  { to: "/unidades", icon: Building2, label: "Unidades", testid: "nav-units", adminOnly: true },
  { to: "/usuarios", icon: UserCog, label: "Usuários", testid: "nav-users", adminOnly: true },
  { to: "/avaliacoes", icon: Trophy, label: "Avaliações", testid: "nav-staff-ratings", adminOnly: true },
  { to: "/configuracoes", icon: SettingsIcon, label: "Configurações", testid: "nav-settings", adminOnly: true },
  { to: "/migracao-nextfit", icon: Database, label: "Migração Next Fit", testid: "nav-nextfit-migration", adminOnly: true },
];

const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(() => isMobile() && !sessionStorage.getItem("af_panel_seen"));
  const location = useLocation();
  const perms = user?.permissions || [];
  const adminLimited = user?.role === "admin" && perms.length > 0;
  const allowed = (path) => !adminLimited || perms.includes(path);

  const handleLogout = () => { logout(); navigate("/login"); };
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => { sessionStorage.setItem("af_panel_seen", "1"); }, []);
  const visible = nav.filter(n => !(n.adminOnly && user?.role !== "admin") && !(n.hideForAdmin && user?.role === "admin")
    && !(user?.role !== "admin" && !n.hideForAdmin && perms.length > 0 && !perms.includes(n.to)) && allowed(n.to));

  const navPaths = nav.map(n => n.to);
  const current = navPaths.filter(p => p !== "/" && location.pathname.startsWith(p)).sort((a, b) => b.length - a.length)[0] || (location.pathname === "/" ? "/" : null);
  if (adminLimited && current && !allowed(current)) {
    const first = nav.find(n => !n.hideForAdmin && perms.includes(n.to));
    if (first && first.to !== current) return <Navigate to={first.to} replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0B]">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 af-mobile-header bg-black text-white flex items-center justify-between px-4 z-40" data-testid="mobile-top-bar">
        <div className="flex items-center gap-2">
          <button onClick={() => setPanel(true)} className="-ml-2 p-2.5 rounded-lg active:bg-white/10" aria-label="Abrir menu" data-testid="mobile-menu-toggle"><Menu className="w-6 h-6"/></button>
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png"
            alt="American Fit" className="h-8"/>
        </div>
        <span className="text-xs text-rose-400 uppercase">{user?.role}</span>
      </div>

      {panel && (
        <MobilePanel items={visible} user={user} theme={theme} onToggleTheme={toggle} onClose={() => setPanel(false)}
          onHome={() => { setPanel(false); navigate("/"); }} onLogout={handleLogout}/>
      )}
      <aside className={`fixed left-0 top-0 h-screen w-64 bg-black text-white z-[60] transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 flex flex-col af-safe-top`}>
        <div className="flex items-center justify-center mb-4 pt-5 px-5 shrink-0">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png"
            alt="American Fit" className="w-full max-w-[180px]" />
        </div>

        <nav className="space-y-1 flex-1 overflow-y-auto overscroll-contain px-5 pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
          {nav.map((n) => {
            if (n.adminOnly && user?.role !== "admin") return null;
            if (n.hideForAdmin && user?.role === "admin") return null;
            // Custom permissions: if user has explicit permissions list, only show those items
            // BUT sempre mostra items hideForAdmin (Avisos) para não-admin — é a tela inicial deles.
            if (user?.role !== "admin" && !n.hideForAdmin && perms.length > 0 && !perms.includes(n.to)) return null;
            if (!allowed(n.to)) return null;
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive ? "bg-rose-600 text-white font-semibold" : "text-slate-300 hover:bg-slate-900 hover:text-white"
                  }`
                }>
                <Icon className="w-4 h-4" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="pt-4 pb-5 px-5 border-t border-slate-800 shrink-0">
          <InstallAppButton />
          <button
            onClick={toggle}
            data-testid="theme-toggle-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-slate-900 hover:text-white transition-all mb-2"
          >
            {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400"/> : <Moon className="w-4 h-4 text-sky-300"/>}
            <span>{theme === "dark" ? "Modo Claro" : "Modo Escuro"}</span>
            <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${theme === "dark" ? "bg-amber-400/20 text-amber-300" : "bg-sky-400/20 text-sky-300"}`}>{theme === "dark" ? "ON" : "OFF"}</span>
          </button>
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold" data-testid="user-profile-badge">{user?.name}</div>
            <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-neutral-900">
            <LogOut className="w-4 h-4 mr-2"/> Sair
          </Button>
        </div>
      </aside>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-[50] bg-[#070A12] border-t border-white/10 shadow-[0_-8px_24px_rgba(0,0,0,.45)]" data-testid="mobile-bottom-bar"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)" }}>
        <div className="w-full max-w-[640px] mx-auto px-[clamp(12px,4.5vw,24px)] pt-2 grid grid-cols-2 gap-3">
          <button onClick={() => setPanel(true)} data-testid="mobile-bottom-home" className="h-11 rounded-xl bg-rose-600 active:bg-rose-700 text-white font-bold text-base">Início</button>
          <button onClick={handleLogout} data-testid="mobile-bottom-logout" className="h-11 rounded-xl border border-white/10 bg-[#111827] text-white font-bold text-base flex items-center justify-center gap-2"><LogOut className="w-5 h-5"/>Sair</button>
        </div>
      </div>
      <main className="lg:ml-64 min-h-screen af-main-offset pb-[calc(env(safe-area-inset-bottom,0px)+72px)] lg:pb-0">
        {/* Banner fixo mostrando a unidade do usuário logado */}
        {user?.unit_name && (
          <div
            className="sticky af-sticky-top z-30 bg-gradient-to-r from-rose-600 via-rose-500 to-amber-500 text-white border-b-2 border-rose-800 shadow-md"
            data-testid="unit-banner"
          >
            <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-[1800px] px-4 sm:px-6 py-2 flex items-center gap-3">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 drop-shadow"/>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">Você está logado em</div>
                <div className="font-heading text-lg sm:text-2xl md:text-3xl font-black uppercase leading-tight truncate drop-shadow" data-testid="unit-banner-name">
                  {user.unit_name}
                </div>
              </div>
              <div className="hidden sm:block text-right text-xs opacity-90">
                <div className="font-semibold truncate max-w-[200px]">{user.name}</div>
                <div className="uppercase tracking-wider text-[10px]">{user.role}</div>
              </div>
            </div>
          </div>
        )}
        {user?.role === "admin" && !user?.unit_name && (
          <div
            className="sticky af-sticky-top z-30 bg-gradient-to-r from-slate-900 via-slate-800 to-black text-white border-b-2 border-rose-700 shadow-md"
            data-testid="unit-banner"
          >
            <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-[1800px] px-4 sm:px-6 py-2 flex items-center gap-3">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400 drop-shadow"/>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-bold text-rose-300">Acesso administrativo</div>
                <div className="font-heading text-lg sm:text-2xl md:text-3xl font-black uppercase leading-tight truncate drop-shadow" data-testid="unit-banner-name">
                  Todas as unidades
                </div>
              </div>
              <div className="hidden sm:block text-right text-xs opacity-90">
                <div className="font-semibold truncate max-w-[200px]">{user.name}</div>
                <div className="uppercase tracking-wider text-[10px] text-rose-300">{user.role}</div>
              </div>
            </div>
          </div>
        )}
        <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-[1800px] p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10 2xl:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
